/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   บันทึกจำนวนที่ส่งแล้วของ Job Order (รายรายการสินค้า)

   ใช้ API แยก (jo/shipped.php) ไม่ผ่านหน้าจอแก้ Job Order
   เพราะการแก้ JO ปกติจะออกเวอร์ชันใหม่ทุกครั้ง หนักเกินไป
   สำหรับงานที่ต้องทยอยบันทึกยอดส่งบ่อย ๆ
============================================================ */
import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  InputNumber,
  Button,
  Space,
  Spin,
  message,
  Typography,
  Tag,
  Alert,
} from "antd";
import { TbTruckDelivery } from "react-icons/tb";

import JobOrderService from "../../../../service/ERP/JobOrder.service";
import { formatMoney } from "../../../../utils/util";

const joService = JobOrderService();
const num = (v) => Number(v || 0);

export default function ModalJoShipped({ show, close, jocode, onSaved }) {
  const [openModal, setOpenModal] = useState(show);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [header, setHeader] = useState({});
  const [rows, setRows] = useState([]);

  const handleClose = () => setTimeout(() => close(false), 140);

  useEffect(() => {
    if (!openModal || !jocode) return;
    setLoading(true);
    joService
      .shippedGet(jocode)
      .then((res) => {
        const { header: h, detail } = res.data?.data || {};
        setHeader(h || {});
        setRows(
          (detail || []).map((d) => ({
            ...d,
            carton: num(d.carton),
            shipped_carton: num(d.shipped_carton),
          }))
        );
      })
      .catch(() => message.error("โหลดข้อมูล Job Order ไม่สำเร็จ"))
      .finally(() => setLoading(false));
  }, [openModal, jocode]);

  const setShipped = (seq, value) =>
    setRows((prev) =>
      prev.map((r) => (r.seq === seq ? { ...r, shipped_carton: num(value) } : r))
    );

  /* เติมจำนวนที่ส่งให้เท่ากับจำนวนที่สั่งทุกบรรทัดในคลิกเดียว */
  const handleShipAll = () =>
    setRows((prev) => prev.map((r) => ({ ...r, shipped_carton: num(r.carton) })));

  const handleClear = () =>
    setRows((prev) => prev.map((r) => ({ ...r, shipped_carton: 0 })));

  const totalCarton = rows.reduce((s, r) => s + num(r.carton), 0);
  const totalShipped = rows.reduce((s, r) => s + num(r.shipped_carton), 0);
  const remain = totalCarton - totalShipped;
  const isDone = totalCarton > 0 && totalShipped >= totalCarton;

  const handleSave = () => {
    setSaving(true);
    joService
      .shippedSave({
        jocode,
        detail: rows.map((r) => ({ seq: r.seq, shipped_carton: num(r.shipped_carton) })),
      })
      .then((res) => {
        const d = res.data?.data || {};
        message.success(
          Number(d.is_done) === 1
            ? "บันทึกแล้ว — ใบนี้ส่งครบทั้งหมด"
            : "บันทึกจำนวนที่ส่งแล้ว"
        );
        if (onSaved) onSaved(d);
        setOpenModal(false);
      })
      .catch((err) => message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ"))
      .finally(() => setSaving(false));
  };

  const columns = [
    { title: "ลำดับ", dataIndex: "seq", key: "seq", width: 60, align: "center" },
    { title: "รหัสสินค้า", dataIndex: "pg_code", key: "pg_code", width: 130 },
    {
      title: "ชื่อสินค้า",
      key: "pg_name",
      render: (_, r) => r.pg_name || r.fg_name || "-",
    },
    {
      title: "สั่ง (กล่อง)",
      dataIndex: "carton",
      key: "carton",
      width: 110,
      align: "right",
      render: (v) => formatMoney(num(v), 0),
    },
    {
      title: "ส่งแล้ว (กล่อง)",
      key: "shipped_carton",
      width: 140,
      align: "right",
      render: (_, r) => (
        <InputNumber
          min={0}
          value={r.shipped_carton}
          style={{ width: "100%" }}
          controls={false}
          onChange={(v) => setShipped(r.seq, v)}
        />
      ),
    },
    {
      title: "คงเหลือ",
      key: "remain",
      width: 110,
      align: "right",
      render: (_, r) => {
        const left = num(r.carton) - num(r.shipped_carton);
        if (left <= 0) return <Tag color="green">ครบ</Tag>;
        return <Typography.Text type="danger">{formatMoney(left, 0)}</Typography.Text>;
      },
    },
  ];

  return (
    <Modal
      open={openModal}
      title={`บันทึกจำนวนที่ส่ง — ${jocode}${header?.cusname ? ` | ${header.cusname}` : ""}`}
      afterClose={handleClose}
      onCancel={() => setOpenModal(false)}
      maskClosable={false}
      style={{ top: 20 }}
      width={950}
      footer={
        <Space>
          <Button onClick={() => setOpenModal(false)}>ปิด</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            บันทึก
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Space wrap>
            <Button
              type="primary"
              ghost
              icon={<TbTruckDelivery style={{ fontSize: "1.1rem" }} />}
              onClick={handleShipAll}
            >
              ส่งครบทั้งหมด
            </Button>
            <Button onClick={handleClear}>ล้างเป็น 0</Button>
          </Space>

          {isDone ? (
            <Alert
              type="success"
              showIcon
              message={`ส่งครบทุกรายการ (${formatMoney(totalShipped, 0)} / ${formatMoney(totalCarton, 0)} กล่อง)`}
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              message={`ส่งแล้ว ${formatMoney(totalShipped, 0)} จาก ${formatMoney(totalCarton, 0)} กล่อง — คงเหลือ ${formatMoney(remain, 0)}`}
            />
          )}

          <Table
            bordered
            size="small"
            rowKey="seq"
            dataSource={rows}
            columns={columns}
            pagination={false}
            scroll={{ y: "45vh" }}
          />
        </Space>
      </Spin>
    </Modal>
  );
}
